package fr.manyverse.app.presentation.components.gm_screen

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Inventory2
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.CompositingStrategy
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.model.PreMadeCharacter
import fr.manyverse.app.domain.util.toDisplayNameFromFileName
import fr.manyverse.app.presentation.components.CharacterAvatar
import fr.manyverse.app.presentation.components.character_sheet.CharacterSheetEditor
import fr.manyverse.app.presentation.components.character_sheet.rememberCharacterSheetEditorState
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.components.ImagePickerButton
import fr.manyverse.app.presentation.components.inventory.InventoryItemFormDialog
import fr.manyverse.app.presentation.components.premade.ImportItemsFromChestDialog
import fr.manyverse.app.presentation.components.premade.PreMadeInventoryRow
import fr.manyverse.app.presentation.components.premade.PreMadeItemDraft
import fr.manyverse.app.presentation.components.premade.displaySignedUrl
import fr.manyverse.app.presentation.components.premade.toDraft
import fr.manyverse.app.presentation.components.rememberImageSelection
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.PreMadeCharactersViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.add_premade
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.character_name
import manyverse.shared.generated.resources.character_name_hint
import manyverse.shared.generated.resources.create
import manyverse.shared.generated.resources.description
import manyverse.shared.generated.resources.edit_premade
import manyverse.shared.generated.resources.image_optional
import manyverse.shared.generated.resources.premade_add_item
import manyverse.shared.generated.resources.premade_copy_sheet
import manyverse.shared.generated.resources.premade_copy_sheet_action
import manyverse.shared.generated.resources.premade_copy_sheet_title
import manyverse.shared.generated.resources.premade_delete_item
import manyverse.shared.generated.resources.premade_edit_item
import manyverse.shared.generated.resources.premade_import_from_chest
import manyverse.shared.generated.resources.premade_inventory_section
import manyverse.shared.generated.resources.save
import org.jetbrains.compose.resources.stringResource
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

@OptIn(ExperimentalUuidApi::class)
@Composable
fun PreMadeCharacterFormDialog(
    viewModel: PreMadeCharactersViewModel,
    existing: PreMadeCharacter? = null,
    isFreeConstruction: Boolean = false,
    isCharacterSheetAvailable: Boolean = false,
    onDismiss: () -> Unit
) {
    val isEdit = existing != null

    var name by remember { mutableStateOf(existing?.name.orEmpty()) }
    var story by remember { mutableStateOf(existing?.story.orEmpty()) }
    val editorState = rememberCharacterSheetEditorState(
        initial = existing?.sheet ?: CharacterSheet(),
        resetKey = existing?.id,
        // Le MJ définit la valeur de départ des jauges du pré-tiré, même sur une jauge existante.
        allowInitialEdit = true
    )
    val imageSelection = rememberImageSelection { image ->
        if (name.isBlank()) name = image.fileName.toDisplayNameFromFileName()
    }

    val createState by viewModel.createState.collectAsStateWithLifecycle()
    val updateState by viewModel.updateState.collectAsStateWithLifecycle()
    val activeState = if (isEdit) updateState else createState
    val isLoading = activeState is AsyncUiState.Loading

    LaunchedEffect(activeState) {
        if (activeState is AsyncUiState.Success) {
            if (isEdit) viewModel.resetUpdateState() else viewModel.resetCreateState()
            onDismiss()
        }
    }

    val displayedImageUrl = imageSelection.displayUrl(existing?.signedImageUrl)
    val showSheetEditor = isFreeConstruction && isCharacterSheetAvailable

    // Pre-mades with a non-empty sheet (excluding the current one being edited)
    val listState by viewModel.listState.collectAsStateWithLifecycle()
    val preMadesWithSheet = remember(listState, existing?.id) {
        (listState as? AsyncUiState.Success)?.data.orEmpty().filter { pm ->
            pm.id != existing?.id &&
            (pm.sheet.gauges.isNotEmpty() || pm.sheet.characteristics.isNotEmpty() ||
                pm.sheet.skills.isNotEmpty() || pm.sheet.powers.isNotEmpty())
        }
    }

    var showCopyDialog by remember { mutableStateOf(false) }

    // Inventaire de départ du pré-tiré (brouillons persistés à la sauvegarde).
    var itemDrafts by remember(existing?.id) {
        mutableStateOf(existing?.inventory?.map { it.toDraft() } ?: emptyList())
    }
    var inventoryExpanded by remember { mutableStateOf(true) }
    var showItemForm by remember { mutableStateOf(false) }
    var editItemTarget by remember { mutableStateOf<PreMadeItemDraft?>(null) }
    var showImportDialog by remember { mutableStateOf(false) }
    val gmChestItems by viewModel.gmChestItems.collectAsStateWithLifecycle()

    AlertDialog(
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        modifier = Modifier.clearFocusOnTap(),
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = {
            Text(stringResource(if (isEdit) Res.string.edit_premade else Res.string.add_premade))
        },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        ImagePickerButton(
                            currentImageUrl = displayedImageUrl,
                            selectedImageData = imageSelection.selected,
                            onPickImage = imageSelection::pick,
                            onRemoveImage = imageSelection::clear,
                            enabled = !isLoading
                        )
                        Text(
                            text = stringResource(Res.string.image_optional),
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }

                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text(stringResource(Res.string.character_name)) },
                    placeholder = { Text(stringResource(Res.string.character_name_hint)) },
                    singleLine = true,
                    enabled = !isLoading,
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    modifier = Modifier.fillMaxWidth()
                )
                OutlinedTextField(
                    value = story,
                    onValueChange = { story = it },
                    label = { Text(stringResource(Res.string.description)) },
                    minLines = 3,
                    maxLines = 6,
                    enabled = !isLoading,
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                    modifier = Modifier.fillMaxWidth()
                )

                HorizontalDivider()

                // Inventaire de départ : boutons d'ajout + section dépliable des objets.
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedButton(
                        onClick = { showItemForm = true },
                        enabled = !isLoading,
                        modifier = Modifier.weight(1f)
                    ) {
                        Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                        Text(
                            text = stringResource(Res.string.premade_add_item),
                            modifier = Modifier.padding(start = 4.dp)
                        )
                    }
                    OutlinedButton(
                        onClick = { showImportDialog = true },
                        enabled = !isLoading,
                        modifier = Modifier.weight(1f)
                    ) {
                        Icon(Icons.Default.Inventory2, contentDescription = null, modifier = Modifier.size(18.dp))
                        Text(
                            text = stringResource(Res.string.premade_import_from_chest),
                            modifier = Modifier.padding(start = 4.dp)
                        )
                    }
                }

                if (itemDrafts.isNotEmpty()) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { inventoryExpanded = !inventoryExpanded },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = "${stringResource(Res.string.premade_inventory_section)} (${itemDrafts.size})",
                                style = MaterialTheme.typography.titleSmall,
                                modifier = Modifier.weight(1f)
                            )
                            Icon(
                                imageVector = if (inventoryExpanded) Icons.Default.KeyboardArrowUp
                                else Icons.Default.KeyboardArrowDown,
                                contentDescription = null
                            )
                        }
                        if (inventoryExpanded) {
                            itemDrafts.forEach { draft ->
                                // Objet importé du coffre : résout l'URL signée de la source pour l'aperçu.
                                var importedSignedUrl by remember(draft.id, draft.copyFromPath) {
                                    mutableStateOf<String?>(null)
                                }
                                LaunchedEffect(draft.copyFromPath) {
                                    importedSignedUrl = draft.copyFromPath?.let {
                                        viewModel.getSignedImageUrl(it).getOrNull()
                                    }
                                }
                                PreMadeInventoryRow(
                                    name = draft.name,
                                    category = draft.category,
                                    quantity = draft.quantity,
                                    signedImageUrl = draft.displaySignedUrl ?: importedSignedUrl,
                                    localImageBytes = draft.newImage?.bytes
                                ) {
                                    IconButton(
                                        onClick = { editItemTarget = draft },
                                        enabled = !isLoading
                                    ) {
                                        Icon(
                                            Icons.Default.Edit,
                                            contentDescription = stringResource(Res.string.premade_edit_item)
                                        )
                                    }
                                    IconButton(
                                        onClick = { itemDrafts = itemDrafts.filterNot { it.id == draft.id } },
                                        enabled = !isLoading
                                    ) {
                                        Icon(
                                            Icons.Default.Delete,
                                            contentDescription = stringResource(Res.string.premade_delete_item),
                                            tint = MaterialTheme.colorScheme.error
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                if (showSheetEditor) {
                    HorizontalDivider()

                    if (preMadesWithSheet.isNotEmpty()) {
                        OutlinedButton(
                            onClick = { showCopyDialog = true },
                            enabled = !isLoading,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Icon(
                                Icons.Default.ContentCopy,
                                contentDescription = null,
                                modifier = Modifier.size(18.dp)
                            )
                            Text(
                                text = stringResource(Res.string.premade_copy_sheet),
                                modifier = Modifier.padding(start = 8.dp)
                            )
                        }
                    }

                    CharacterSheetEditor(
                        state = editorState,
                        enabled = !isLoading
                    )
                }

                if (activeState is AsyncUiState.Error) {
                    Text(
                        text = activeState.message,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        },
        confirmButton = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(onClick = onDismiss, enabled = !isLoading) {
                    Text(stringResource(Res.string.cancel))
                }
                Button(
                    onClick = {
                        val built = if (showSheetEditor) {
                            editorState.validateAndBuild() ?: return@Button
                        } else {
                            existing?.sheet ?: CharacterSheet()
                        }
                        if (existing != null) {
                            viewModel.update(
                                id = existing.id,
                                name = name,
                                story = story,
                                imageData = imageSelection.selected,
                                removeImage = imageSelection.isRemovalRequested,
                                currentImageUrl = existing.imageUrl,
                                sheet = built,
                                itemDrafts = itemDrafts,
                                previousInventory = existing.inventory
                            )
                        } else {
                            viewModel.create(name, story, imageSelection.selected, built, itemDrafts)
                        }
                    },
                    enabled = name.isNotBlank() && !isLoading
                ) {
                    Text(stringResource(if (isEdit) Res.string.save else Res.string.create))
                }
            }
        }
    )

    if (showItemForm || editItemTarget != null) {
        val target = editItemTarget
        InventoryItemFormDialog(
            title = stringResource(
                if (target != null) Res.string.premade_edit_item else Res.string.premade_add_item
            ),
            itemKey = target?.id,
            initialName = target?.name ?: "",
            initialCategory = target?.category ?: ItemCategory.WEAPON,
            initialQuantity = target?.quantity ?: 1,
            currentImageUrl = target?.displaySignedUrl,
            onConfirm = { name, category, quantity, imageData ->
                if (target != null) {
                    itemDrafts = itemDrafts.map { draft ->
                        when {
                            draft.id != target.id -> draft
                            imageData != null -> draft.copy(
                                name = name,
                                category = category,
                                quantity = quantity,
                                newImage = imageData,
                                copyFromPath = null,
                                removeImage = false
                            )
                            else -> draft.copy(name = name, category = category, quantity = quantity)
                        }
                    }
                    editItemTarget = null
                } else {
                    itemDrafts = itemDrafts + PreMadeItemDraft(
                        id = Uuid.random().toString(),
                        name = name,
                        category = category,
                        quantity = quantity,
                        newImage = imageData
                    )
                    showItemForm = false
                }
            },
            onDismiss = {
                showItemForm = false
                editItemTarget = null
            }
        )
    }

    if (showImportDialog) {
        ImportItemsFromChestDialog(
            chestItems = gmChestItems,
            getSignedImageUrl = { path -> viewModel.getSignedImageUrl(path).getOrNull() },
            onImport = { imported ->
                itemDrafts = itemDrafts + imported.map { gm ->
                    PreMadeItemDraft(
                        id = Uuid.random().toString(),
                        name = gm.name,
                        category = gm.category,
                        quantity = gm.quantity,
                        copyFromPath = gm.imageUrl
                    )
                }
                showImportDialog = false
            },
            onDismiss = { showImportDialog = false }
        )
    }

    if (showCopyDialog) {
        CopySheetFromPreMadeDialog(
            preMades = preMadesWithSheet,
            onSelect = { selected ->
                editorState.load(selected.sheet)
                showCopyDialog = false
            },
            onDismiss = { showCopyDialog = false }
        )
    }
}

@Composable
private fun CopySheetFromPreMadeDialog(
    preMades: List<PreMadeCharacter>,
    onSelect: (PreMadeCharacter) -> Unit,
    onDismiss: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.premade_copy_sheet_title)) },
        text = {
            val listState = rememberLazyListState()
            Box {
                LazyColumn(
                    state = listState,
                    modifier = Modifier.scrollFadingEdges(listState),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    itemsIndexed(preMades, key = { _, pm -> pm.id }) { index, pm ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            CharacterAvatar(
                                imageUrl = pm.signedImageUrl,
                                characterName = pm.name,
                                size = 40.dp
                            )
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    text = pm.name,
                                    style = MaterialTheme.typography.bodyLarge
                                )
                                TextButton(
                                    onClick = { onSelect(pm) },
                                    contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp)
                                ) {
                                    Text(stringResource(Res.string.premade_copy_sheet_action))
                                }
                            }
                        }
                        if (index < preMades.lastIndex) {
                            HorizontalDivider(modifier = Modifier.padding(top = 8.dp))
                        }
                    }
                }
                if (listState.canScrollForward) {
                    Icon(
                        imageVector = Icons.Default.KeyboardArrowDown,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.align(Alignment.BottomCenter)
                    )
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.cancel))
            }
        }
    )
}

/**
 * Fades the top/bottom edge of a scrollable list when content is hidden
 * beyond it, so the user can tell the list scrolls.
 */
private fun Modifier.scrollFadingEdges(state: LazyListState): Modifier = this
    .graphicsLayer { compositingStrategy = CompositingStrategy.Offscreen }
    .drawWithContent {
        drawContent()
        val fadeHeight = 32.dp.toPx().coerceAtMost(size.height / 3)
        if (state.canScrollBackward) {
            drawRect(
                brush = Brush.verticalGradient(
                    colors = listOf(Color.Transparent, Color.Black),
                    endY = fadeHeight
                ),
                blendMode = BlendMode.DstIn
            )
        }
        if (state.canScrollForward) {
            drawRect(
                brush = Brush.verticalGradient(
                    colors = listOf(Color.Black, Color.Transparent),
                    startY = size.height - fadeHeight,
                    endY = size.height
                ),
                blendMode = BlendMode.DstIn
            )
        }
    }
